import { rem } from '@triumph/design-system';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useRef, useState } from 'react';


import { RelatedOriginationsApplicant } from '../../Applicant.types';
import { ApplicantKeyValuePair, ApplicantKeyValueTable } from '../ApplicantKeyValuePair';
import {
    ApplicantItemWrapper,
    EndOfListMessage,
    ExternalLinkIcon,
    LoadMoreTrigger,
    MatchedOnList,
    SkeletonItem,
    SkeletonWrapper,
} from './ApplicantRelatedAccounts.StyledComponents';
import { RelatedAccountsCard } from './RelatedAccountsCard';
import { ORIGINATIONS_PAGINATION } from './constants';

interface SectionProps extends React.ComponentPropsWithRef<'div'> {
    accountsType: string;
    accounts: RelatedOriginationsApplicant[];
    isLoading?: boolean;
    isLoadingError?: boolean;
    onRetry?: () => void;
}

export const OriginationsApplicantsSection = ({
    accountsType,
    accounts,
    isLoading = false,
    isLoadingError = false,
    onRetry,
    ...props
}: SectionProps) => {
    // Pagination state
    const [visibleCount, setVisibleCount] = useState(ORIGINATIONS_PAGINATION.INITIAL_PAGE_SIZE);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Ref for the intersection observer target
    const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

    // Sort accounts by date descending
    const sortedAccounts = [...accounts].sort(
        (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    );

    // Get currently visible items
    const visibleAccounts = sortedAccounts.slice(0, visibleCount);
    const hasMore = visibleCount < sortedAccounts.length;

    // Load more items function
    const loadMore = () => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);

        // Simulate slight delay for skeleton animation
        setTimeout(() => {
            setVisibleCount((prev) =>
                Math.min(prev + ORIGINATIONS_PAGINATION.PAGE_SIZE, sortedAccounts.length)
            );
            setIsLoadingMore(false);
        }, 300);
    };

    // Set up Intersection Observer for infinite scroll
    useEffect(() => {
        const target = loadMoreTriggerRef.current;
        if (!target || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoadingMore) {
                    loadMore();
                }
            },
            {
                root: null,
                rootMargin: '100px', // Trigger 100px before reaching the bottom
                threshold: 0.1,
            }
        );

        observer.observe(target);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoadingMore, visibleCount]);

    // Reset visible count when accounts array changes
    useEffect(() => {
        setVisibleCount(ORIGINATIONS_PAGINATION.INITIAL_PAGE_SIZE);
        setIsLoadingMore(false);
    }, [accounts.length]);

    const renderApplicant = (applicant: RelatedOriginationsApplicant, i: number) => {
        const itemKey = `${accountsType}-related-account-${i}`;
        const companyDisplay = applicant.partnerGroup
            ? `${applicant.partnerGroup} - ${applicant.companyName}`
            : applicant.companyName;

        return (
            <ApplicantItemWrapper key={itemKey} data-testid={itemKey}>
                <ApplicantKeyValueTable>
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-company-name`}
                        label="Company Name"
                        value={
                            <a href={`/applicant/${applicant.applicantId}`} target="_blank" rel="noreferrer">
                                {companyDisplay} <ExternalLinkIcon>{'\u{238B}'}</ExternalLinkIcon>
                            </a>
                        }
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-matched-on`}
                        label="Matched On"
                        value={
                            <MatchedOnList>
                                {applicant.matchedOn.map((match) => (
                                    <li key={match}>{match}</li>
                                ))}
                            </MatchedOnList>
                        }
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-status`}
                        label="Status"
                        value={
                            <MatchedOnList>
                                {applicant.status.map((s) => (
                                    <li key={s}>{s}</li>
                                ))}
                            </MatchedOnList>
                        }
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-date-created`}
                        label="Date Created"
                        value={applicant.dateCreated}
                    />
                </ApplicantKeyValueTable>
            </ApplicantItemWrapper>
        );
    };

    return (
        <RelatedAccountsCard
            {...props}
            data-testid={`${accountsType}-related-account`}
            title={`${accountsType} Applications`}
            count={sortedAccounts.length}
            isLoading={isLoading}
            isLoadingError={isLoadingError}
            onRetry={onRetry}
            emptyMessage={`No related ${accountsType} applications found.`}
            errorMessage={`Could not load ${accountsType} applications.`}
        >
            <>
                {/* Render visible applicants */}
                {visibleAccounts.map(renderApplicant)}

                {/* Skeleton loaders while loading more */}
                {isLoadingMore &&
                    Array.from({ length: ORIGINATIONS_PAGINATION.SKELETON_COUNT }).map((_, i) => (
                        // eslint-disable-next-line @eslint-react/no-array-index-key -- fixed-length placeholders
                        <SkeletonWrapper key={`skeleton-${i}`}>
                            {/* Company name skeleton */}
                            <SkeletonItem marginBottom={rem(16)}>
                                <Skeleton width="60%" height={rem(20)} />
                            </SkeletonItem>
                            {/* Matched on skeleton */}
                            <SkeletonItem marginBottom={rem(8)}>
                                <Skeleton width="40%" height={rem(14)} />
                            </SkeletonItem>
                            {/* Status skeleton */}
                            <SkeletonItem marginBottom={rem(8)}>
                                <Skeleton width="35%" height={rem(14)} />
                            </SkeletonItem>
                            {/* Date created skeleton */}
                            <SkeletonItem>
                                <Skeleton width="45%" height={rem(14)} />
                            </SkeletonItem>
                        </SkeletonWrapper>
                    ))}

                {/* Intersection observer trigger */}
                {hasMore && !isLoadingMore && (
                    <LoadMoreTrigger ref={loadMoreTriggerRef} data-testid="load-more-trigger" />
                )}

                {/* End of list indicator */}
                {!hasMore && sortedAccounts.length > ORIGINATIONS_PAGINATION.INITIAL_PAGE_SIZE && (
                    <EndOfListMessage data-testid="end-of-list">
                        End of results ({sortedAccounts.length} total)
                    </EndOfListMessage>
                )}
            </>
        </RelatedAccountsCard>
    );
};
